// src/services/CloudFileService.js - Final version with server-side stamp calculation
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import {
  collection,
  doc,
  setDoc,
  updateDoc,
  getDoc,
  getDocs,
  deleteDoc,
  query,
  orderBy,
  onSnapshot,
  writeBatch,
  serverTimestamp
} from 'firebase/firestore';
import { PDFDocument } from 'pdf-lib';
import { storage, db } from '../firebase/firebase';
import { captureError, addBreadcrumb, trackOperation } from '../utils/errorHandling';

/**
 * Validate that a value is a non-empty string
 */
function validateString(value, paramName) {
  if (!value || typeof value !== 'string' || value.trim() === '') {
    throw new Error(`${paramName} must be a non-empty string, got: ${typeof value} - ${value}`);
  }
  return value.trim();
}

const MAX_FILE_SIZE = 200 * 1024 * 1024; // 200MB - matches Storage rules

const ACCEPTED_EXTENSIONS = new Set([
  'pdf', 'jpg', 'jpeg', 'png', 'gif', 'webp',
  'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx',
  'eml', 'msg', 'txt', 'rtf'
]);

const UNCONVERTIBLE_EXTENSIONS = new Set([
  'mp4', 'mov', 'avi', 'webm', 'wmv', 'mkv', 'flv',
  'mp3', 'wav', 'aac', 'flac', 'ogg', 'm4a',
  'exe', 'bat', 'sh', 'dll', 'app',
  'zip', 'rar', '7z', 'tar', 'gz',
  'iso', 'dmg',
  'ttf', 'otf', 'woff', 'woff2',
  'stl', 'obj', 'dwg',
  'db', 'sqlite'
]);

/**
 * Validate a file before uploading - checks type and size client-side
 * to give immediate feedback without burning a Firebase Storage request
 */
function validateFileBeforeUpload(file) {
  const allowedTypes = [
    'application/pdf',
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'message/rfc822',
    'application/vnd.ms-outlook',
    'text/plain',
    'application/rtf'
  ];

  if (!allowedTypes.includes(file.type)) {
    const extension = file.name.split('.').pop().toUpperCase();
    const reason = `${extension} files are not supported. Supported formats: PDF, Word, Excel, PowerPoint, images, and plain text.`;
    const err = new Error(`"${file.name}" could not be uploaded. ${reason}`);
    err.isUserValidationError = true;
    err.reason = reason;
    throw err;
  }

  if (file.size > MAX_FILE_SIZE) {
    const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
    const reason = `The file is ${sizeMB}MB, which exceeds the 200MB limit. Please compress or split the file.`;
    const err = new Error(`"${file.name}" could not be uploaded. ${reason}`);
    err.isUserValidationError = true;
    err.reason = reason;
    throw err;
  }
}

/**
 * Update the case's lastModified timestamp
 * This should be called whenever files are added, removed, or modified
 */
async function updateCaseLastModified(caseId) {
  const validCaseId = validateString(caseId, 'caseId');
  
  try {
    const caseRef = doc(db, 'cases', validCaseId);
    await updateDoc(caseRef, {
      lastModified: serverTimestamp()
    });
    // console.log(`Updated lastModified for case: ${validCaseId}`);
  } catch (error) {
    // Log but don't throw - we don't want file operations to fail if this fails
    console.warn('Failed to update case lastModified:', error);
  }
}

/**
 * URL Cache with TTL to prevent redundant getDownloadURL calls
 * Reduces Firebase Storage load during concurrent operations
 */
const urlCache = new Map(); // Map<storagePath, { url, timestamp }>
const URL_CACHE_TTL = 3600000; // 1 hour

/**
 * Get cached URL or fetch new one
 */
async function getCachedAuthenticatedUrl(storagePath) {
  const now = Date.now();
  const cached = urlCache.get(storagePath);

  // Return cached URL if it's still fresh
  if (cached && (now - cached.timestamp) < URL_CACHE_TTL) {
    // console.log('📦 Using cached URL for:', storagePath);
    return cached.url;
  }

  // Remove stale cache entry
  if (cached) {
    urlCache.delete(storagePath);
  }

  // Fetch new URL from Firebase Storage
  try {
    const storageRef = ref(storage, storagePath);
    const downloadURL = await getDownloadURL(storageRef);

    // Cache the new URL
    urlCache.set(storagePath, {
      url: downloadURL,
      timestamp: now
    });

    // console.log('🔗 Generated new authenticated URL for:', storagePath);
    return downloadURL;
  } catch (error) {
    // Don't cache errors - let them propagate
    throw error;
  }
}

/**
 * Service for managing file uploads, conversion, and retrieval
 * Server calculates all stamp data during processing
 */
const CloudFileService = {
  // Returns { valid: true } or { valid: false, reason, errorType, errorSubtype } — never throws
  checkFile(file) {
    if (file.size > MAX_FILE_SIZE) {
      const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
      return {
        valid: false,
        reason: `${file.name} is ${sizeMB}MB and exceeds the 200MB size limit.`,
        errorType: 'file_too_large',
      };
    }

    const ext = (file.name.split('.').pop() || '').toLowerCase();
    if (!ACCEPTED_EXTENSIONS.has(ext)) {
      const errorSubtype = UNCONVERTIBLE_EXTENSIONS.has(ext) ? 'unconvertible' : 'convertible';
      return {
        valid: false,
        reason: `${ext.toUpperCase() || 'Unknown'} files are not supported.`,
        errorType: 'unsupported_format',
        errorSubtype,
      };
    }

    return { valid: true };
  },

  // Returns true if file is an encrypted PDF that cannot be loaded
  async isPdfEncrypted(file) {
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    if (ext !== 'pdf') return false;
    try {
      const bytes = await file.arrayBuffer();
      await PDFDocument.load(bytes);
      return false;
    } catch (e) {
      return e.message?.toLowerCase().includes('encrypt') || e.name === 'EncryptedPDFError';
    }
  },

  // Creates a Firestore error doc (no storage upload) and returns the doc ID
  async createErrorDoc(caseId, file, errorType, errorSubtype) {
    const validCaseId = validateString(caseId, 'caseId');

    let order = 0;
    try {
      const existingFiles = await this.getFiles(validCaseId);
      if (existingFiles.length > 0) {
        const maxOrder = Math.max(...existingFiles.map(f => (typeof f.order === 'number' ? f.order : -1)));
        order = maxOrder + 1;
      }
    } catch (e) {
      console.warn('Could not calculate order for error doc:', e);
    }

    const docId = Date.now().toString(36) + Math.random().toString(36).substring(2, 9);
    const fileRef = doc(db, 'cases', validCaseId, 'files', docId);
    const data = {
      id: docId,
      name: file.name,
      status: 'client_error',
      errorType,
      storageRef: null,
      order,
      createdAt: serverTimestamp(),
      caseId: validCaseId,
    };
    if (errorSubtype) data.errorSubtype = errorSubtype;

    await setDoc(fileRef, data);
    await updateCaseLastModified(validCaseId);
    return docId;
  },

  // Replace an existing doc with a new file.
  // On validation failure, creates a new error doc and deletes the old one.
  // On success, re-uses the existing doc ID (preserves order).
  async replaceFile(caseId, existingDocId, newFile, userId) {
    const validCaseId = validateString(caseId, 'caseId');
    const validDocId = validateString(existingDocId, 'existingDocId');
    const validUserId = validateString(userId, 'userId');

    // Check validity first
    const check = this.checkFile(newFile);
    if (!check.valid) {
      await this.createErrorDoc(validCaseId, newFile, check.errorType, check.errorSubtype || null);
      await deleteDoc(doc(db, 'cases', validCaseId, 'files', validDocId));
      await updateCaseLastModified(validCaseId);
      return { success: false, errorType: check.errorType };
    }

    // Check for encryption
    const encrypted = await this.isPdfEncrypted(newFile);
    if (encrypted) {
      await this.createErrorDoc(validCaseId, newFile, 'restricted_pdf', null);
      await deleteDoc(doc(db, 'cases', validCaseId, 'files', validDocId));
      await updateCaseLastModified(validCaseId);
      return { success: false, errorType: 'restricted_pdf' };
    }

    // Valid file — read existing doc for order, then overwrite with new upload
    const existingDocRef = doc(db, 'cases', validCaseId, 'files', validDocId);
    const existingSnap = await getDoc(existingDocRef);
    const existingOrder = existingSnap.exists() ? (existingSnap.data().order ?? 0) : 0;

    validateFileBeforeUpload(newFile);

    const sanitizedFileName = newFile.name.replace(/[^a-zA-Z0-9.-]/g, '_');
    const storagePath = `uploads/${validUserId}/${validCaseId}/${validDocId}_${sanitizedFileName}`;

    const storageRef = ref(storage, storagePath);
    const uploadResult = await uploadBytes(storageRef, newFile);
    const downloadURL = await getDownloadURL(uploadResult.ref);

    urlCache.set(storagePath, { url: downloadURL, timestamp: Date.now() });

    await setDoc(existingDocRef, {
      id: validDocId,
      originalName: newFile.name,
      displayName: newFile.name.replace(/\.[^.]+$/i, ''),
      fileSize: newFile.size,
      fileType: newFile.type,
      originalPath: storagePath,
      originalUrl: downloadURL,
      uploadedAt: new Date(),
      caseId: validCaseId,
      userId: validUserId,
      order: existingOrder,
      status: 'processing',
    });

    await updateCaseLastModified(validCaseId);
    return { success: true };
  },

  /**
   * Upload files to Firebase Storage - FIXED VERSION
   * More robust order calculation with detailed logging
   */
  async uploadFiles(caseId, files, userId, onProgressCallback = () => {}) {
    
    addBreadcrumb('Starting file upload', 'file-upload', {
      caseId,
      fileCount: files.length,
      userId,
    });
    
    const validCaseId = validateString(caseId, 'caseId');
    const validUserId = validateString(userId, 'userId');
    
    if (!files || !Array.isArray(files) || files.length === 0) {
      throw new Error('Files must be a non-empty array');
    }

    if (typeof onProgressCallback !== 'function') {
      onProgressCallback = () => {};
    }
    
    try {
      return await trackOperation('file_upload', async () => {
        const fileRecords = [];
        
        // IMPROVED: Get existing files and calculate starting order more carefully
        let startingOrder = 0;
        try {
          const existingFiles = await this.getFiles(validCaseId);
          
          // FIXED: Find the maximum order value and add 1
          // This is more robust than using length, especially if there are gaps or duplicates
          if (existingFiles.length > 0) {
            const maxOrder = Math.max(...existingFiles.map(f => 
              typeof f.order === 'number' ? f.order : -1
            ));
            startingOrder = maxOrder + 1;
            /*
            console.log(`📊 Upload order calculation:`, {
              existingFileCount: existingFiles.length,
              maxExistingOrder: maxOrder,
              startingOrder: startingOrder,
              existingOrders: existingFiles.map(f => f.order)
            });
            */
          } else {
            console.log('📊 No existing files, starting at order 0');
          }
          
        } catch (error) {
          console.warn('Could not get existing files count, starting at order 0:', error);
          startingOrder = 0;
        }
        
        // Process each file
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          const assignedOrder = startingOrder + i;

          onProgressCallback(`Uploading file ${i + 1} of ${files.length}: ${file.name}`, Math.round((i / files.length) * 100));

          // Generate a unique file ID
          const fileId = Date.now().toString(36) + Math.random().toString(36).substring(2, 5);

          // Create storage paths - Include userId in path
          const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
          const storagePath = `uploads/${validUserId}/${validCaseId}/${fileId}_${sanitizedFileName}`;

          validateFileBeforeUpload(file);

          // Create Firestore doc BEFORE uploading to storage so the Cloud Run
          // storage trigger always finds an existing document to update.
          const fileRef = doc(db, 'cases', validCaseId, 'files', fileId);
          const fileData = {
            id: fileId,
            originalName: file.name,
            displayName: file.name.replace(/\.[^.]+$/i, ''),
            fileSize: file.size,
            fileType: file.type,
            originalPath: storagePath,
            uploadedAt: new Date(),
            caseId: validCaseId,
            userId: validUserId,
            order: assignedOrder,
            status: 'processing'
          };

          try {
            await setDoc(fileRef, fileData);
          } catch (firestoreError) {
            console.error(`Error creating Firestore document for file ${fileId}:`, firestoreError);
            throw new Error(`Failed to create file record: ${firestoreError.message}`);
          }

          // Upload to storage — triggers Cloud Run, which can now find the doc
          let downloadURL;
          try {
            const storageRef = ref(storage, storagePath);
            const uploadResult = await uploadBytes(storageRef, file);
            downloadURL = await getDownloadURL(uploadResult.ref);
          } catch (storageError) {
            // Clean up the Firestore doc so we don't leave an orphan
            try { await deleteDoc(fileRef); } catch (_) {}
            throw storageError;
          }

          // Cache the URL to prevent redundant getDownloadURL calls in the listener
          urlCache.set(storagePath, { url: downloadURL, timestamp: Date.now() });

          // Patch the URL into Firestore now that we have it
          try {
            await updateDoc(fileRef, { originalUrl: downloadURL });
          } catch (e) {
            console.warn('Could not patch originalUrl into Firestore doc:', e);
          }

          fileRecords.push({
            id: fileId,
            ...fileData,
            originalUrl: downloadURL,
            file: { url: downloadURL, name: file.name }
          });
        }
        
        onProgressCallback('Upload completed! Server is processing files...', 100);
        await updateCaseLastModified(validCaseId);
        return fileRecords;
      });
    } catch (error) {
    // Only report unexpected system errors — not user validation failures
    if (!error.isUserValidationError) {
      captureError(error, {
        operation: 'uploadFiles',
        caseId: validCaseId,
        userId: validUserId,
        fileCount: files.length,
        fileTypes: files.map(f => f.type).join(','),
      });
    }
    throw error; // Re-throw so calling code can handle
    }
  },

  /**
   * Get all files for a case - FIXED VERSION
   * Uses a query without orderBy to ensure all files are counted
   */
  async getFiles(caseId) {
    const validCaseId = validateString(caseId, 'caseId');
    
    try {
      // console.log('Getting files for case:', validCaseId);
      
      // Create collection reference with validated caseId
      const filesRef = collection(db, 'cases', validCaseId, 'files');
      
      // FIXED: Don't use orderBy in the query - get ALL files
      // Then sort in application code to ensure we don't miss any files
      const snapshot = await getDocs(filesRef);
      const files = [];
      
      // Get authenticated download URLs for all files
      const filesWithAuthUrls = await Promise.all(
        snapshot.docs.map(async (docSnap) => {
          const data = docSnap.data();
          const fileData = { id: docSnap.id, ...data };

          // Generate authenticated URL for searchable PDF
          if (data.searchablePdfPath) {
            try {
              const searchableUrl = await this.getAuthenticatedUrl(data.searchablePdfPath);
              if (searchableUrl) {
                fileData.searchablePdfUrl = searchableUrl;
              }
            } catch (error) {
              console.warn(`Could not get auth URL for searchable PDF ${data.searchablePdfPath}:`, error);
            }
          }

          return fileData;
        })
      );

      files.push(...filesWithAuthUrls);
      
      // Sort by order field, with fallback for files missing order
      files.sort((a, b) => {
        const orderA = typeof a.order === 'number' ? a.order : 999999;
        const orderB = typeof b.order === 'number' ? b.order : 999999;
        return orderA - orderB;
      });
      
      // console.log(`Retrieved ${files.length} files for case ${validCaseId}`);
      // console.log('File orders:', files.map(f => ({ id: f.id, order: f.order, name: f.originalName })));
      
      return files;
    } catch (error) {
      console.error('Error getting files:', error);
      throw error;
    }
  },

  /**
   * Get authenticated download URL for a file path
   * Uses caching to prevent redundant Firebase Storage requests
   */
  async getAuthenticatedUrl(storagePath) {
    try {
      return await getCachedAuthenticatedUrl(storagePath);
    } catch (error) {
      // Only capture if it's unexpected (not just missing file)
      if (error.code !== 'storage/object-not-found') {
        captureError(error, {
          operation: 'getAuthenticatedUrl',
          filePath: storagePath,
        });
      }
      throw error;
    }
  },
  /**
   * Update file metadata - handles all stamp position updates
   * Now handles complete stamp position data from server or user changes
   */
  async updateFileMetadata(caseId, fileId, updates) {
    const validCaseId = validateString(caseId, 'caseId');
    const validFileId = validateString(fileId, 'fileId');
    
    if (!updates || typeof updates !== 'object') {
      throw new Error('Updates must be an object');
    }
    
    try {
      // console.log(`Updating file ${validFileId} in case ${validCaseId}:`, updates);
      
      // Optional: Log stamp location updates
      if (updates.stampLocation) {
        // console.log(`📍 Stamp location update (page units):`, updates.stampLocation);
      }
      
      const fileRef = doc(db, 'cases', validCaseId, 'files', validFileId);
      await updateDoc(fileRef, updates);
      
      // console.log(`File ${validFileId} updated successfully`);
      await updateCaseLastModified(validCaseId);
      return true;
    } catch (error) {
      console.error('Error updating file metadata:', error);
      throw error;
    }
  },
  /**
   * Update file order
   */
  async updateFileOrder(caseId, fileId, newOrder) {
    return this.updateFileMetadata(caseId, fileId, { order: newOrder });
  },

  /**
   * Update file display name
   */
  async updateFileName(caseId, fileId, newName) {
    const validName = validateString(newName, 'newName');
    return this.updateFileMetadata(caseId, fileId, { displayName: validName });
  },

  /**
   * Delete a file
   * Handles race condition where Firestore document may not exist yet due to write delays
   */
  async deleteFile(caseId, fileId, userId) {

    addBreadcrumb('Deleting file', 'file-management', {
      caseId,
      fileId,
      userId,
    });

    const validCaseId = validateString(caseId, 'caseId');
    const validFileId = validateString(fileId, 'fileId');
    const validUserId = validateString(userId, 'userId');
    try {
      // Get the file data first
      const fileRef = doc(db, 'cases', validCaseId, 'files', validFileId);
      const fileSnap = await getDoc(fileRef);

      if (!fileSnap.exists()) {
        // GRACEFUL HANDLING: Document doesn't exist in Firestore
        // This can occur due to Firestore write-read consistency lag:
        // - User deletes file before setDoc() writes are committed
        // - Document was already deleted in another session
        // - Document failed to persist initially
        //
        // Since the file doesn't exist, from the user's perspective it's already deleted.
        // We return success (idempotent delete) rather than throwing an error.

        console.warn(
          `⚠️ File ${validFileId} not found in Firestore during deletion attempt. ` +
          `This likely indicates a write-read consistency lag. ` +
          `Treating as successful deletion since file is effectively gone.`
        );

        addBreadcrumb('File not found during deletion', 'file-management', {
          caseId: validCaseId,
          fileId: validFileId,
          reason: 'Firestore write-read consistency lag (document does not exist)'
        });

        // Still try to update case timestamp to reflect the deletion attempt
        try {
          await updateCaseLastModified(validCaseId);
        } catch (timestampError) {
          console.warn('Could not update case timestamp:', timestampError);
          // Don't fail the operation for this
        }

        return true;
      }

      const fileData = fileSnap.data();

      // Client-side error docs have no storage — skip straight to Firestore deletion
      if (fileData.storageRef === null) {
        await deleteDoc(fileRef);
        await updateCaseLastModified(validCaseId);
        return true;
      }

      // Delete from storage
      try {
        if (fileData.originalPath) {
          const originalRef = ref(storage, fileData.originalPath);
          await deleteObject(originalRef);
          // console.log('Deleted original file from storage:', fileData.originalPath);
        }

        if (fileData.pdfPath && fileData.pdfPath !== fileData.originalPath) {
          const pdfRef = ref(storage, fileData.pdfPath);
          await deleteObject(pdfRef);
          // console.log('Deleted PDF file from storage:', fileData.pdfPath);
        }

        if (fileData.searchablePdfPath && fileData.searchablePdfPath !== fileData.pdfPath) {
          const searchableRef = ref(storage, fileData.searchablePdfPath);
          await deleteObject(searchableRef);
          // console.log('Deleted searchable PDF from storage:', fileData.searchablePdfPath);
        }
      } catch (storageError) {
        console.warn('Error deleting from storage (continuing with Firestore deletion):', storageError);
      }

      // Delete from Firestore
      await deleteDoc(fileRef);
      // console.log(`File ${validFileId} deleted successfully`);

      await updateCaseLastModified(validCaseId);

      return true;
    } catch (error) {
      captureError(error, {
        operation: 'deleteFile',
        caseId: validCaseId,
        fileId: validFileId,
        userId: validUserId,
      });
      throw error;
    }
  },

  /**
   * Set up real-time listener for file changes
   */
  listenToFiles(caseId, onUpdate) {
    const validCaseId = validateString(caseId, 'caseId');
    
    if (typeof onUpdate !== 'function') {
      throw new Error('onUpdate must be a function');
    }
    
    try {
      // console.log('Setting up file listener for case:', validCaseId);
      
      // Create collection reference with validated caseId
      const filesRef = collection(db, 'cases', validCaseId, 'files');
      
      const unsubscribe = onSnapshot(filesRef, (snapshot) => {
        if (snapshot.empty) {
          console.log('No files found for this case');
          onUpdate({});
          return;
        }

        snapshot.docChanges().forEach(change => {
          const data = change.doc.data();
          const fileId = change.doc.id;
          
          // UPDATED: Log server processing status updates
          if (change.type === 'modified' && data.status) {
            // console.log(`📊 File ${fileId} status: ${data.status}`, data.displayName || data.originalName);
            
            // UPDATED: Log stamp location instead of stamp position
            if (data.stampLocation?.x !== undefined && data.stampLocation?.y !== undefined) {
              // console.log(`✅ Server calculated stamp location (page units): x=${data.stampLocation.x.toFixed(1)}, y=${data.stampLocation.y.toFixed(1)}`);
            }
          }
          
          onUpdate(fileId, data, change.type);
        });
      }, error => {
        console.error('Error in files listener:', error);
      });
      
      return unsubscribe;
    } catch (error) {
      console.error('Error setting up file listener:', error);
      return () => {};
    }
  },

  /**
   * Bulk update file orders
   */
  async updateFileOrders(caseId, fileOrderUpdates) {
    const validCaseId = validateString(caseId, 'caseId');
    
    if (!Array.isArray(fileOrderUpdates) || fileOrderUpdates.length === 0) {
      throw new Error('fileOrderUpdates must be a non-empty array');
    }
    
    try {
      const batch = writeBatch(db);
      
      fileOrderUpdates.forEach(({ fileId, order }) => {
        const validFileId = validateString(fileId, 'fileId');
        if (typeof order !== 'number') {
          throw new Error(`Order must be a number for file ${validFileId}`);
        }
        
        const fileRef = doc(db, 'cases', validCaseId, 'files', validFileId);
        batch.update(fileRef, { order });
      });
      
      await batch.commit();
      // console.log(`Updated ${fileOrderUpdates.length} file orders`);
      return true;
    } catch (error) {
      console.error('Error updating file orders:', error);
      throw error;
    }
  }
};

export default CloudFileService;